import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import useEffectOnce from 'react-use';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { ToastProvider } from 'react-toast-notifications';
import { TourProvider } from '@reactour/tour';
import ThemeContext from '../contexts/themeContext';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import { Toast, ToastContainer } from '../components/bootstrap/Toasts';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import AsideRoutes from '../layout/Aside/AsideRoutes';
import ConfigContext from '../contexts/configContext';
import axios from 'axios';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import { demoPagesMenu } from '../menu';
import AuthContext from '../contexts/authContext';
import LoadingPage from '../components/LoadingPage';
import useFacebookPixel from "../hooks/facebookPixelHook";
import { ClickToCallProvider } from '../contexts/clickToCallContext';
import { set } from 'date-fns';
const App = () => {
	const { userData } = useContext(AuthContext);
	const location = useLocation();
	getOS();

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};

	useEffect(() => {
		document.title = process.env.REACT_APP_COMPANY_NAME || 'ARK CRM';
	}, []);

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
		};
	}, [darkModeStatus]);

	/**
	 * Full Screen
	 */
	// @ts-ignore
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});
	// track facebook pages
	const facebookPixelId = process.env.REACT_APP_FB_PIXEL_ID;
	const { trackEvent: trackFacebookEvent } = useFacebookPixel(facebookPixelId);

	useEffect(() => {
		trackFacebookEvent('PageView');
	}, [trackFacebookEvent, location.pathname]);

	

	// ✅ Redirect only if current path is "/"

	if (location.pathname === '/') {
		if (userData.userTypeId === 1) {
			return <Navigate to='/client' replace />;
		} else if (userData.userTypeId === 2) {
			return <Navigate to='/client' replace />;
		} else if (userData.userTypeId === 3) {
			return <Navigate to='/admin' replace />;
		} else {
			return <Navigate to='/auth-pages/login' replace />;
		}
	}

	return (
		<ThemeProvider theme={theme}>
			<ToastProvider components={{ ToastContainer, Toast }}>
				<ClickToCallProvider>
					<TourProvider
						steps={steps}
						styles={styles}
						showNavigation={false}
						showBadge={false}>
						<div
							ref={ref}
							className='app'
							style={{
								backgroundColor: fullScreenStatus ? 'var(--bs-body-bg)' : undefined,
								zIndex: fullScreenStatus ? 1 : undefined,
								overflow: fullScreenStatus ? 'scroll' : undefined,
							}}>
							<AsideRoutes />
							<Wrapper />
						</div>
						<Portal id='portal-notification'>
							<ReactNotifications />
						</Portal>
					</TourProvider>
				</ClickToCallProvider>
			</ToastProvider>
		</ThemeProvider>
	);
};

export default App;
